import {
  DocumentStopEntry,
  LoadConfirmationStop,
  LoadConfirmationDocumentData,
  LoadConfirmationPageSlice,
} from '../types'

/** Usable vertical space on A4 (px at ~96dpi), below header and above footer. */
const A4_USABLE_CONTENT_PX = 920

const NOTES_CHARS_PER_LINE = 68
const STOP_NOTES_CHARS_PER_LINE = 100

const HEIGHT = {
  fullHeader: 118,
  miniHeader: 34,
  carrier: 98,
  stopBase: 72,
  stopNotesLabel: 16,
  stopNotesLine: 16,
  notesSectionHeader: 28,
  notesLine: 18,
  footer: 36,
} as const

/** Word-aware wrap so long paragraphs estimate height closer to rendered layout. */
export function wrapTextToLines(text: string, charsPerLine: number): string[] {
  const result: string[] = []
  const paragraphs = text.replace(/\r\n/g, '\n').split('\n')

  for (const para of paragraphs) {
    if (para.trim() === '') {
      result.push('')
      continue
    }

    const words = para.split(/\s+/).filter(Boolean)
    let current = ''

    for (const word of words) {
      if (current.length === 0) {
        if (word.length <= charsPerLine) {
          current = word
        } else {
          let rest = word
          while (rest.length > charsPerLine) {
            result.push(rest.slice(0, charsPerLine))
            rest = rest.slice(charsPerLine)
          }
          current = rest
        }
        continue
      }

      const candidate = `${current} ${word}`
      if (candidate.length <= charsPerLine) {
        current = candidate
      } else {
        result.push(current)
        if (word.length <= charsPerLine) {
          current = word
        } else {
          let rest = word
          while (rest.length > charsPerLine) {
            result.push(rest.slice(0, charsPerLine))
            rest = rest.slice(charsPerLine)
          }
          current = rest
        }
      }
    }

    if (current) {
      result.push(current)
    }
  }

  return result
}

function countWrappedLines(text: string, charsPerLine: number): number {
  const lines = wrapTextToLines(text, charsPerLine)
  if (lines.length === 0) return 0
  return lines.length
}

function estimateStopHeight(stop: LoadConfirmationStop): number {
  const noteLines = countWrappedLines(stop.notes ?? '', STOP_NOTES_CHARS_PER_LINE)
  const notesHeight =
    noteLines > 0
      ? HEIGHT.stopNotesLabel + noteLines * HEIGHT.stopNotesLine
      : HEIGHT.stopNotesLabel
  return HEIGHT.stopBase + notesHeight
}

function buildStopEntries(data: LoadConfirmationDocumentData): DocumentStopEntry[] {
  const entries: DocumentStopEntry[] = []
  let number = 1

  data.pickups.forEach((stop) => {
    entries.push({ kind: 'shipper', stop, stopNumber: number++ })
  })
  data.deliveries.forEach((stop) => {
    entries.push({ kind: 'consignee', stop, stopNumber: number++ })
  })

  return entries
}

type MutablePage = {
  headerVariant: 'full' | 'mini'
  showCarrier: boolean
  stops: DocumentStopEntry[]
  notesText?: string
  notesContinued?: boolean
  usedHeight: number
}

function createPage(headerVariant: 'full' | 'mini', showCarrier: boolean): MutablePage {
  const overhead =
    headerVariant === 'full'
      ? HEIGHT.fullHeader + (showCarrier ? HEIGHT.carrier : 0)
      : HEIGHT.miniHeader

  return {
    headerVariant,
    showCarrier,
    stops: [],
    usedHeight: overhead,
  }
}

function remainingHeight(page: MutablePage): number {
  return A4_USABLE_CONTENT_PX - HEIGHT.footer - page.usedHeight
}

function startContinuationPage(pages: MutablePage[]): MutablePage {
  const page = createPage('mini', false)
  pages.push(page)
  return page
}

function appendNotesLines(
  pages: MutablePage[],
  currentPage: MutablePage,
  allLines: string[]
): MutablePage {
  let page = currentPage
  let lineIndex = 0
  let placedNotesBefore = false

  while (lineIndex < allLines.length) {
    const headerHeight = placedNotesBefore ? 0 : HEIGHT.notesSectionHeader
    let remaining = remainingHeight(page)

    if (remaining < headerHeight + HEIGHT.notesLine) {
      page = startContinuationPage(pages)
      remaining = remainingHeight(page)
    }

    const linesAvailable = Math.max(
      1,
      Math.floor((remaining - headerHeight) / HEIGHT.notesLine)
    )
    const chunk = allLines.slice(lineIndex, lineIndex + linesAvailable)
    lineIndex += chunk.length

    page.notesText = chunk.join('\n')
    page.notesContinued = placedNotesBefore
    page.usedHeight += headerHeight + chunk.length * HEIGHT.notesLine
    placedNotesBefore = true

    if (lineIndex < allLines.length) {
      page = startContinuationPage(pages)
    }
  }

  return page
}

export function paginateDocument(
  data: LoadConfirmationDocumentData
): LoadConfirmationPageSlice[] {
  const allStops = buildStopEntries(data)
  const trimmedNotes = (data.notes ?? '').trim()
  const notesLines = trimmedNotes
    ? wrapTextToLines(trimmedNotes, NOTES_CHARS_PER_LINE)
    : []

  const pages: MutablePage[] = []
  let currentPage = createPage('full', true)
  pages.push(currentPage)

  for (const entry of allStops) {
    const stopHeight = estimateStopHeight(entry.stop)
    if (remainingHeight(currentPage) < stopHeight) {
      currentPage = startContinuationPage(pages)
    }
    currentPage.stops.push(entry)
    currentPage.usedHeight += stopHeight
  }

  if (notesLines.length > 0) {
    currentPage = appendNotesLines(pages, currentPage, notesLines)
  }

  const totalPages = pages.length || 1
  return pages.map((page, idx) => ({
    headerVariant: page.headerVariant,
    showCarrier: page.showCarrier,
    stops: page.stops,
    showNotes: !!page.notesText,
    notesText: page.notesText,
    notesContinued: page.notesContinued,
    pageNumber: idx + 1,
    totalPages,
  }))
}
